/**
 * SplashScreen.tsx
 *
 * Shows once per app session on cold start.
 * Logo bounces in → "Happy hour has been Unlocked" text reveals → fades out.
 *
 * Uses sessionStorage so it shows once per cold start only.
 * Clears when the app process is killed, so it appears again on next open.
 */

import React, { useState, useEffect } from 'react'

const SESSION_KEY = 'hhu_splash_v1'

export function SplashScreen() {
  const [visible, setVisible] = useState(
    () => !sessionStorage.getItem(SESSION_KEY)
  )
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (!visible) return
    sessionStorage.setItem(SESSION_KEY, '1')

    const fadeTimer = setTimeout(() => setFading(true), 2300)
    const hideTimer = setTimeout(() => setVisible(false), 2800)
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer) }
  }, []) // eslint-disable-line

  if (!visible) return null

  return (
    <>
      <style>{`
        @keyframes hhu-logo-in {
          0%   { opacity: 0; transform: scale(0.82); }
          65%  { opacity: 1; transform: scale(1.04); }
          80%  { opacity: 1; transform: scale(0.98); }
          100% { opacity: 1; transform: scale(1.0);  }
        }
        @keyframes hhu-text-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes hhu-fade-in  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes hhu-fade-out { from { opacity: 1; } to { opacity: 0; } }

        .hhu-splash {
          position: fixed; inset: 0; z-index: 9999;
          background: #000;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          gap: 0;
        }
        .hhu-splash-logo {
          width: 160px; height: 160px;
          border-radius: 32px;
          display: block;
          opacity: 0;
          animation: hhu-logo-in 0.6s cubic-bezier(0.34,1.4,0.64,1) 0.15s forwards;
        }
        .hhu-splash-text {
          margin-top: 24px;
          text-align: center;
          opacity: 0;
          animation: hhu-text-up 0.5s ease 0.9s forwards;
        }
        .hhu-splash-label {
          font-size: 11px; font-weight: 700;
          letter-spacing: 0.2em; text-transform: uppercase;
          color: rgba(255,255,255,0.4);
          margin-bottom: 6px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .hhu-splash-title {
          font-size: 26px; font-weight: 700; font-style: italic;
          font-family: 'Playfair Display', Georgia, serif;
          background: linear-gradient(to right, #F5A200, #E8264A);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.3px;
        }
      `}</style>

      <div
        className="hhu-splash"
        style={{
          animation: fading
            ? 'hhu-fade-out 0.5s ease forwards'
            : 'hhu-fade-in 0.2s ease',
        }}
      >
        <img
          src="/logo512.png"
          alt="Happy Hour Unlocked"
          className="hhu-splash-logo"
        />

        <div className="hhu-splash-text">
          <div className="hhu-splash-label">Happy hour has been</div>
          <div className="hhu-splash-title">Unlocked 🍺</div>
        </div>
      </div>
    </>
  )
}
